import React from 'react';
import { formatISAAmount, getCurrentUKTaxYear } from '../../../../utils/taxYearHelpers';
import { useUnifiedAnalytics } from '../../../../contexts/AnalyticsWrapper';
import { getIsaRemainingAllowance, getIsaProgressPercentage } from '../../utils/isaHelpers';
import infoIcon from '../../../../assets/images/info-icon.svg';

/** @import { IsaAllowanceWidgetProps } from '../../DashboardNewTypes' */

/**
 * ISA Allowance Widget component
 * Displays the customer's ISA allowance status and remaining deposit capacity
 * @param {IsaAllowanceWidgetProps} props
 */
const IsaAllowanceWidget = ({ isaAllowance, showIsaTooltip, onToggleIsaTooltip }) => {
    const analytics = useUnifiedAnalytics();

    const holdAllowance = isaAllowance?.holdAllowance ?? 0;
    const hasHold = holdAllowance > 0;
    const remainingAllowance = Math.max(0, getIsaRemainingAllowance(isaAllowance) - holdAllowance);
    const progressPercentage = getIsaProgressPercentage(isaAllowance, holdAllowance);
   
    // Format taxYear from backend ("2025-2026") to full display format ("2025/2026")
    const displayTaxYearFull = isaAllowance?.taxYear
        ? isaAllowance.taxYear.replace('-', '/')
        : getCurrentUKTaxYear().replace(/\/(\d{2})$/, '/20$1');
    
    // Format taxYear to short display format ("2025/26") for tooltip
    const displayTaxYear = isaAllowance?.taxYear
        ? `${isaAllowance.taxYear.split('-')[0]}/${isaAllowance.taxYear.split('-')[1]?.slice(-2)}`
        : getCurrentUKTaxYear();
    
    const handleInfoClick = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                "ISA allowance info tooltip",
                window.location.href,
                "ISA allowance component",
                "",
                "",
                {},
                showIsaTooltip ? "click" : "click",
                `dashboard|click|isa-allowance-tooltip`,
                "ISA Allowance Feature"
            );
        }
        onToggleIsaTooltip();
    };
    
    return (
        <div className="new-dashboard__isa-allowance">
            <div className="isa-allowance">
                <div className="isa-allowance__header">
                    <span className="isa-allowance__label">
                        ISA allowance {displayTaxYearFull} 
                    </span>
                    <button
                        className="isa-allowance__info-icon"
                        onClick={handleInfoClick}
                        aria-label="ISA allowance information"
                        type="button"
                    >
                        <img src={infoIcon} alt="ISA allowance information" />
                    </button>
                    {showIsaTooltip && (
                        <div className="isa-allowance__info-popup" role="tooltip">
                            Your ISA allowance is the total amount you can save tax-free across all your ISAs in a tax year. For {displayTaxYear}, the limit is £20,000. This tracker only shows what you&apos;ve saved with us, so if you also have ISAs elsewhere, you&apos;ll need to keep track of those to stay within your total allowance.
                        </div>
                    )}
                </div>
                <div className="isa-allowance__amount">
                    You can still deposit {formatISAAmount(remainingAllowance)}
                </div>
                <div className="isa-allowance__progress">
                    <div 
                        className="isa-allowance__progress-bar"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
                {hasHold && (
                    <div className="isa-allowance__hold-info-box">
                        <svg className="isa-allowance__hold-info-box__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path fillRule="evenodd" clipRule="evenodd" d="M12 3V3C16.971 3 21 7.029 21 12V12C21 16.971 16.971 21 12 21V21C7.029 21 3 16.971 3 12V12C3 7.029 7.029 3 12 3Z" stroke="#964E1E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 12.5V7.5" stroke="#964E1E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M11.999 16C11.861 16 11.749 16.112 11.75 16.25C11.75 16.388 11.862 16.5 12 16.5C12.138 16.5 12.25 16.388 12.25 16.25C12.25 16.112 12.138 16 11.999 16" stroke="#964E1E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="isa-allowance__hold-info-box__text">
                            This includes {formatISAAmount(holdAllowance)} we expect to receive from your transfer request. The amount you can deposit may change once your transfer is complete.
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IsaAllowanceWidget;
